import * as React from 'react';
import {useContext, useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import {ThemeProvider} from '@mui/material/styles';
import {useTheme} from "@mui/material";
import GoogleButton from "react-google-button";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup
} from "firebase/auth";
import {AppContext} from "../services/AppContext";
import {useNavigate} from "react-router-dom";
import PasswordResetModal from "./PasswordResetModal";
import {db} from "../services/firebaseApp";
import {doc, getDoc, setDoc} from "firebase/firestore";
import { Alert, CircularProgress } from "@mui/material";

export function LoginPage({user}) {
  const navigate = useNavigate()

  const provider = new GoogleAuthProvider();
  const theme = useTheme();
  const auth = getAuth()
  const [checked, setChecked] = React.useState(false);
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [pwdResetModal, setPwdResetModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);

  const handleCheckbox = (event) => {
    setChecked(event.target.checked);
  };

  const {state: {currentUser}, dispatch} = useContext(AppContext);

  useEffect(() => {

  }, [])

  async function handleLoginGoogle() {
    setLoading(true);
    setError("");
    
    try {
      await signInWithPopup(auth, provider);
      await hydrateUserData();
    } catch (error) {
      console.error("Google auth error:", error);
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-in cancelled. Please try again.");
      } else if (error.code === 'auth/popup-blocked') {
        setError("Pop-up blocked. Please allow pop-ups for this site.");
      } else {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function hydrateUserData() {
    const docRef = doc(db, "users", auth.currentUser.uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      dispatch({currentUser: docSnap.data()})
      navigate('/dashboard')
    } else {
      try {
        await setDoc(doc(db, "users", auth.currentUser.uid), {
          uid: auth.currentUser.uid,
          joined: Date.now(),
          images: [],
          files: []
        });
        dispatch({
          currentUser: {
            uid: auth.currentUser.uid,
            joined: Date.now(),
            images: [],
            files: []
          }
        })
        navigate('/dashboard')
      } catch (e) {
        console.log(e)
      }
    }
  }

  async function handleLoginEmailPassword() {
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (isSignUp) {
        // Sign up new user
        await createUserWithEmailAndPassword(auth, email, password);
        await hydrateUserData();
      } else {
        // Sign in existing user
        await signInWithEmailAndPassword(auth, email, password);
        await hydrateUserData();
      }
    } catch (error) {
      console.error("Auth error:", error);
      
      if (error.code === 'auth/user-not-found') {
        setError("No account found with this email. Click 'Sign Up' to create a new account.");
      } else if (error.code === 'auth/wrong-password') {
        setError("Incorrect password. Please try again.");
      } else if (error.code === 'auth/email-already-in-use') {
        setError("An account already exists with this email. Please sign in instead.");
        setIsSignUp(false);
      } else if (error.code === 'auth/weak-password') {
        setError("Password should be at least 6 characters long.");
      } else if (error.code === 'auth/invalid-email') {
        setError("Please enter a valid email address.");
      } else {
        setError(error.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    console.log('user login')
    console.log(user)

    async function userData() {
      await hydrateUserData()
    }

    if (user) {
      userData()
    }
  }, [user])

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="xs" sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }}>
          <Typography 
            variant={"h3"} 
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b8cf8 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Tomira Storage
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ mb: 3, textAlign: 'center' }}
          >
            Secure cloud storage for all your files
          </Typography>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%', 
                mb: 2,
                borderRadius: 2
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={2} alignItems={"center"} alignContent={"center"}
                justifyContent={"center"}
                sx={{width: '100%'}}>
            <Grid item xs={12}>
              <GoogleButton
                onClick={() => handleLoginGoogle()}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  boxShadow: '0 2px 4px 0 rgba(0,0,0,0.25)',
                  opacity: loading ? 0.7 : 1
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                my: 2,
                '&:before, &:after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e2e8f0',
                }
              }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ px: 2 }}
                >
                  OR
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                value={email}
                onChange={e => {setEmail(e.target.value); setError("");}}
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              <TextField
                value={password}
                onChange={e => {setPassword(e.target.value); setError("");}}
                type={checked ? "text" : "password"}
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                id="password"
                autoComplete="current-password"
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
          <Grid container alignItems={"center"} alignContent={"center"} justifyContent={"center"}>
            <Grid item xs={12} sm={6}>
              <Box display="flex" justifyContent="flex-start">
                <FormControlLabel
                  control={<Checkbox
                    checked={checked}
                    onChange={handleCheckbox}
                    inputProps={{'aria-label': 'controlled'}}
                  />}
                  label="Show Password"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography 
                variant="body2" 
                onClick={() => setPwdResetModal(true)}
                sx={{
                  cursor: 'pointer',
                  color: '#6366f1',
                  fontWeight: 500,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Forgot password?
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ width: '100%', mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => handleLoginEmailPassword()}
              disabled={loading}
              sx={{
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                background: isSignUp ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' : 'linear-gradient(135deg, #6366f1 0%, #8b8cf8 100%)',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                '&:hover': {
                  background: isSignUp ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                },
                '&:disabled': {
                  background: '#9ca3af',
                  color: '#ffffff'
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isSignUp ? 'Sign Up' : 'Sign In'
              )}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={() => {setIsSignUp(!isSignUp); setError("");}}
              disabled={loading}
              sx={{
                fontWeight: 500,
                textTransform: 'none',
                color: '#6366f1',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.05)'
                }
              }}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </Button>
          </Box>
          <PasswordResetModal
            open={pwdResetModal}
            setOpen={setPwdResetModal}
            title={"Reset Password"} description={'Input your email to receive a reset password link'}
            actionIfYes={() => {
              sendPasswordResetEmail(auth, email).then(() => console.log('done'))
            }}
            actionIfNo={() => setPwdResetModal(false)}
            closeBtnText={"cancel"}
            yesBtnText={'reset'}
            value={email}
            setValue={setEmail}
          >
          </PasswordResetModal>
        </Container>
      </ThemeProvider>
    </div>
  );
}
